import React, { useState, useEffect, useMemo } from 'react';
import { Card, Button, Modal, Form, Select, Input, Space, Table, Tag, message, DatePicker, InputNumber, Popconfirm, Typography } from 'antd';
import { PlusOutlined, HistoryOutlined, FileTextOutlined, CalendarOutlined, EditOutlined, ClockCircleOutlined, CheckOutlined, DeleteOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import isoWeek from 'dayjs/plugin/isoWeek';
import api from '../../api/axios';
import { getTerms, getClassrooms } from '../../api/settings';
import useAuthStore from '../../store/authStore';

// /users/:id sayfasi sadece MANAGER_ROLES icin acik (App.js) - bu tab
// ise ogretmenlerin de erisebildigi ogrenci detayinda kullanildigi icin
// "Alan" linki sadece yonetici rolleri icin tiklanabilir gosteriliyor,
// yoksa ogretmen icin kirik/engellenen bir linke donusuyordu.
const CAN_VIEW_USER_DETAIL_ROLES = ['developer_admin', 'super_admin', 'branch_manager'];

dayjs.extend(isBetween);
dayjs.extend(isoWeek);

const { Option } = Select;

const PAYMENT_METHOD_LABEL = {
  cash:        'Nakit',
  transfer:    'Havale/EFT',
  credit_card: 'Kredi Kartı',
  other:       'Diğer',
};

const METHOD_COLORS = {
  cash:        'green',
  transfer:    'blue',
  credit_card: 'purple',
  other:       'default',
};

const buildTimeOptions = (firstSlot, lastSlot) => {
  if (!firstSlot || !lastSlot) return [];
  const [fh, fm] = firstSlot.split(':').map(Number);
  const [lh, lm] = lastSlot.split(':').map(Number);
  const options = [];
  let h = fh, m = fm;
  while (h < lh || (h === lh && m <= lm)) {
    options.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
    m += 60; h += Math.floor(m / 60); m %= 60;
  }
  return options;
};

const StudentChargesTab = ({ studentId, charges: chargesProp, onUpdate, branchId, student, readOnly = false }) => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const canViewUserDetail = CAN_VIEW_USER_DETAIL_ROLES.includes(user?.role);
  const [modalOpen, setModalOpen]             = useState(false);
  const [payments, setPayments]               = useState([]);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [localCharges, setLocalCharges]       = useState(null);
  const charges = localCharges ?? chargesProp;
  const [form] = Form.useForm();

  // Ders ekle
  // Borç düzenleme
  const [editingCharge, setEditingCharge]     = useState(null); // {id, amount}
  const [chargeEditAmt, setChargeEditAmt]     = useState('');
  const [chargeSaving, setChargeSaving]       = useState(false);

  // Ödeme düzenleme
  const [editPayment, setEditPayment]         = useState(null);
  const [paymentForm] = Form.useForm();
  const [paymentSaving, setPaymentSaving]     = useState(false);
  // "Ödeme Al" modalı — paymentSaving BAŞKA bir iş için (kayıtlı ödemeyi
  // düzenleme), karıştırma. Ayrı state olmasının sebebi bu.
  const [odemeAliniyor, setOdemeAliniyor]     = useState(false);

  // Geçmiş dönem manuel borçları (ders seansına bağlı olmayan, elle girilen
  // borç — bkz. StudentManualDebtController). Ekleme/tahsilat/silme sadece
  // super_admin/branch_manager: backend'deki payments.create/edit/delete
  // yetkisiyle birebir aynı, burada sadece görünürlük.
  const [manualDebts, setManualDebts]           = useState([]);
  const [manualDebtsLoading, setManualDebtsLoading] = useState(false);
  const [addDebtModal, setAddDebtModal]         = useState(false);
  const [addDebtSaving, setAddDebtSaving]       = useState(false);
  const [editingDebt, setEditingDebt]           = useState(null); // null = ekleme, kayıt = düzenleme
  const [debtForm] = Form.useForm();
  const [payDebtModal, setPayDebtModal]         = useState({ open: false, record: null, amount: null, saving: false });
  const canManageDebts = !readOnly && ['developer_admin', 'super_admin', 'branch_manager'].includes(user?.role);

  // Ders ekle
  const [lessonModal, setLessonModal]     = useState(false);
  const [lessonDate, setLessonDate]       = useState(null);
  const [lessonClassroom, setLessonClass] = useState(null);
  const [lessonTime, setLessonTime]       = useState(null);
  const [lessonType, setLessonType]       = useState('one_time');
  const [lessonLoading, setLessonLoading] = useState(false);
  const [classrooms, setClassrooms]       = useState([]);
  const [terms, setTerms]                 = useState([]);

  const lessonTimeOptions = useMemo(() => {
    const term = lessonDate
      ? terms.find(t => dayjs(lessonDate).isBetween(t.start_date, t.end_date, 'day', '[]'))
      : terms[0];
    return buildTimeOptions(term?.first_slot_time, term?.last_slot_time);
  }, [lessonDate, terms]);

  const fetchPayments = async () => {
    setPaymentsLoading(true);
    try {
      const res = await api.get(`/students/${studentId}/payments`);
      setPayments(res.data.data || res.data || []);
    } catch {
      setPayments([]);
    } finally {
      setPaymentsLoading(false);
    }
  };

  const fetchChargesSummary = async () => {
    try {
      const res = await api.get(`/students/${studentId}/charges`);
      setLocalCharges(res.data);
    } catch {}
  };

  const fetchManualDebts = async () => {
    setManualDebtsLoading(true);
    try {
      const res = await api.get(`/students/${studentId}/manual-debts`);
      setManualDebts(res.data.data || []);
    } catch {
      setManualDebts([]);
    } finally {
      setManualDebtsLoading(false);
    }
  };

  useEffect(() => { fetchPayments(); }, [studentId]);
  useEffect(() => { fetchManualDebts(); }, [studentId]);

  useEffect(() => {
    if (branchId && !readOnly) {
      Promise.all([
        getClassrooms({ per_page: 100, branch_id: branchId }),
        getTerms({ per_page: 20, is_active: 1, branch_id: branchId }),
      ]).then(([cr, tr]) => {
        setClassrooms(cr.data.data || []);
        setTerms(tr.data.data || []);
      }).catch(() => {});
    }
  }, [branchId, readOnly]);

  const handleAddLesson = async () => {
    if (!lessonDate || !lessonClassroom || !lessonTime) { message.error('Tüm alanları doldurun.'); return; }
    const term = terms.find(t => dayjs(lessonDate).isBetween(t.start_date, t.end_date, 'day', '[]')) || terms[0];
    if (!term) { message.error('Bu tarihe ait aktif dönem bulunamadı.'); return; }
    const [h, m] = lessonTime.split(':').map(Number);
    const endTime = `${String(h + 1).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
    setLessonLoading(true);
    try {
      await api.post('/schedule/enroll', {
        student_id:   parseInt(studentId),
        term_id:      term.id,
        classroom_id: lessonClassroom,
        day_of_week:  dayjs(lessonDate).isoWeekday(),
        start_time:   lessonTime,
        end_time:     endTime,
        type:         lessonType,
        start_date:   lessonDate.format('YYYY-MM-DD'),
      });
      message.success('Ders kaydı oluşturuldu.');
      setLessonModal(false);
      setLessonDate(null); setLessonClass(null); setLessonTime(null); setLessonType('one_time');
      onUpdate();
    } catch (err) {
      message.error(err?.response?.data?.message || 'Ders eklenemedi.');
    } finally {
      setLessonLoading(false);
    }
  };

  const handleSaveCharge = async () => {
    if (!editingCharge) return;
    setChargeSaving(true);
    try {
      await api.put(`/students/${studentId}/charges/${editingCharge.id}`, { amount: chargeEditAmt });
      message.success('Tutar güncellendi.');
      setEditingCharge(null);
      onUpdate();
    } catch (err) {
      message.error(err?.response?.data?.message || 'Güncellenemedi.');
    } finally {
      setChargeSaving(false);
    }
  };

  const handleSavePayment = async (values) => {
    setPaymentSaving(true);
    try {
      await api.put(`/payments/${editPayment.id}`, {
        amount:         parseFloat(values.amount),
        payment_method: values.payment_method,
        paid_at:        values.paid_at.format('YYYY-MM-DD HH:mm:ss'),
        notes:          values.notes || null,
      });
      message.success('Ödeme güncellendi.');
      setEditPayment(null);
      fetchPayments();
      onUpdate();
    } catch (err) {
      message.error(err?.response?.data?.message || 'Güncellenemedi.');
    } finally {
      setPaymentSaving(false);
    }
  };

  // ÇİFT GÖNDERİM KORUMASI — bu formda YOKTU ve canlıda mükerrer ödeme
  // üretti (21.08.2026: aynı öğrenciye aynı dakikada iki kez 700 ₺).
  // Backend'de idempotency kontrolü yok, yani iki tıklama iki KAYIT demek
  // ve ortaya gerçek bir finansal hata çıkıyor.
  const handlePayment = async (values) => {
    if (odemeAliniyor) return;          // çok hızlı ikinci tıklama
    setOdemeAliniyor(true);
    try {
      const amount = parseFloat(values.amount);
      if (amount <= 0) { message.error('Tutar 0\'dan büyük olmalıdır.'); return; }
      await api.post(`/students/${studentId}/payments`, {
        amount,
        payment_method: values.payment_method,
        paid_at:        dayjs().format('YYYY-MM-DD HH:mm:ss'),
        notes:          values.notes,
        period:         values.period || null,
      });
      message.success('Ödeme kaydedildi.');
      setModalOpen(false);
      form.resetFields();
      fetchPayments();
      fetchChargesSummary();
      onUpdate();
    } catch (err) {
      message.error(err?.response?.data?.message || 'Ödeme kaydedilemedi.');
    } finally {
      setOdemeAliniyor(false);
    }
  };

  const handleSaveDebt = async (values) => {
    setAddDebtSaving(true);
    const payload = {
      amount:      parseFloat(values.amount),
      incurred_at: values.incurred_at ? values.incurred_at.format('YYYY-MM-DD') : null,
      description: values.description,
      notes:       values.notes || null,
    };
    try {
      if (editingDebt) {
        await api.put(`/students/${studentId}/manual-debts/${editingDebt.id}`, payload);
        message.success('Borç kaydı güncellendi.');
      } else {
        await api.post(`/students/${studentId}/manual-debts`, payload);
        message.success('Geçmiş dönem borcu eklendi.');
      }
      setAddDebtModal(false);
      setEditingDebt(null);
      debtForm.resetFields();
      fetchManualDebts();
      fetchChargesSummary();
      onUpdate();
    } catch (err) {
      message.error(err?.response?.data?.message || (editingDebt ? 'Güncellenemedi.' : 'Borç eklenemedi.'));
    } finally {
      setAddDebtSaving(false);
    }
  };

  const handlePayDebt = async () => {
    const r = payDebtModal.record;
    if (!r) return;
    setPayDebtModal((s) => ({ ...s, saving: true }));
    try {
      await api.patch(`/manual-debts/${r.id}/pay`, { paid_amount: payDebtModal.amount });
      message.success('Tahsilat kaydedildi.');
      setPayDebtModal({ open: false, record: null, amount: null, saving: false });
      fetchManualDebts();
      fetchChargesSummary();
      onUpdate();
    } catch (err) {
      message.error(err?.response?.data?.message || 'Tahsilat kaydedilemedi.');
      setPayDebtModal((s) => ({ ...s, saving: false }));
    }
  };

  const handleDeleteDebt = async (debt) => {
    try {
      await api.delete(`/students/${studentId}/manual-debts/${debt.id}`);
      message.success('Kayıt silindi.');
      fetchManualDebts();
      fetchChargesSummary();
      onUpdate();
    } catch (err) {
      message.warning(err?.response?.data?.message || 'Silinemedi.');
    }
  };

  const totalCharged = parseFloat(charges?.total_charged || 0);
  const totalDebt    = parseFloat(charges?.total_debt    || 0);
  const totalPaid    = parseFloat(charges?.total_paid    || 0);
  const chargeList   = charges?.charges || [];

  // Net bakiye: ödenen - borçlandırılan (fazla ödeme varsa pozitif)
  const netBalance = totalPaid - totalCharged;

  return (
    <>
      {/* Borç Özeti */}
      <Card
        className="mb-4"
        extra={
          <div className="flex gap-2">
            <Button type="primary" icon={<PlusOutlined />} size="small" onClick={() => setModalOpen(true)}>
              Ödeme Al
            </Button>
          </div>
        }
      >
        <div className="grid grid-cols-3 gap-4">
          <Card size="small" className="text-center">
            <div className="text-2xl font-bold text-orange-500">{totalCharged.toFixed(2)} ₺</div>
            <div className="text-gray-500 text-sm">Toplam Ders Tutarı</div>
          </Card>
          <Card size="small" className="text-center">
            <div className="text-2xl font-bold text-green-500">{totalPaid.toFixed(2)} ₺</div>
            <div className="text-gray-500 text-sm">Yapılan Ödeme</div>
          </Card>
          <Card size="small" className="text-center">
            {netBalance >= 0 ? (
              <>
                <div className="text-2xl font-bold text-blue-500">{netBalance.toFixed(2)} ₺</div>
                <div className="text-gray-500 text-sm">Fazla Ödeme</div>
              </>
            ) : (
              <>
                <div className="text-2xl font-bold text-red-500">{Math.abs(netBalance).toFixed(2)} ₺</div>
                <div className="text-gray-500 text-sm">Kalan Tutar</div>
              </>
            )}
          </Card>
        </div>
        {charges?.note && (
          <div className="mt-3 text-sm text-gray-500 bg-gray-50 p-2 rounded">{charges.note}</div>
        )}
      </Card>

      {/* Borçlandırılan Dersler */}
      {chargeList.length > 0 && (
        <Card
          className="mb-4"
          title={
            <span className="flex items-center gap-2">
              <FileTextOutlined />
              Borçlandırılan Dersler
            </span>
          }
        >
          <Table
            size="small"
            dataSource={chargeList}
            rowKey="id"
            pagination={{ pageSize: 10, defaultCurrent: 1, showSizeChanger: false }}
            locale={{ emptyText: 'Borçlandırma kaydı yok.' }}
            columns={[
              {
                title: 'Tarih',
                dataIndex: 'date',
                render: (v) => v ? dayjs(v).format('DD.MM.YYYY') : '—',
                sorter: (a, b) => dayjs(a.date).unix() - dayjs(b.date).unix(),
                defaultSortOrder: 'descend',
              },
              {
                title: 'Saat',
                dataIndex: 'start_time',
                render: (v) => v ? v.slice(0, 5) : '—',
              },
              {
                title: 'Derslik',
                dataIndex: 'classroom',
                render: (v) => v || '—',
              },
              {
                title: 'Tutar',
                dataIndex: 'amount',
                render: (v, r) => {
                  if (r.is_waived)
                    return <span className="line-through text-gray-400">{parseFloat(v).toFixed(2)} ₺ (Silindi)</span>;
                  if (!readOnly && editingCharge?.id === r.id)
                    return (
                      <Space>
                        <InputNumber
                          size="small"
                          min={0}
                          step={0.01}
                          value={chargeEditAmt}
                          onChange={setChargeEditAmt}
                          suffix="₺"
                          style={{ width: 90 }}
                          autoFocus
                        />
                        <Button size="small" type="primary" loading={chargeSaving} onClick={handleSaveCharge}>Kaydet</Button>
                        <Button size="small" onClick={() => setEditingCharge(null)}>İptal</Button>
                      </Space>
                    );
                  return (
                    <Space>
                      <span className="font-semibold text-orange-600">{parseFloat(v).toFixed(2)} ₺</span>
                      {!readOnly && <Button size="small" icon={<EditOutlined />} onClick={() => { setEditingCharge(r); setChargeEditAmt(parseFloat(v)); }} />}
                    </Space>
                  );
                },
              },
            ]}
          />
        </Card>
      )}

      {/* Geçmiş Dönem Borçları — ders seansına bağlı olmayan, elle girilen borç */}
      {(manualDebts.length > 0 || canManageDebts) && (
        <Card
          className="mb-4"
          title={
            <span className="flex items-center gap-2">
              <ClockCircleOutlined />
              Geçmiş Dönem Borçları
            </span>
          }
          extra={canManageDebts && (
            <Button icon={<PlusOutlined />} size="small" onClick={() => { setEditingDebt(null); debtForm.resetFields(); setAddDebtModal(true); }}>
              Borç Ekle
            </Button>
          )}
        >
          <Table
            size="small"
            dataSource={manualDebts}
            rowKey="id"
            loading={manualDebtsLoading}
            pagination={false}
            locale={{ emptyText: 'Geçmiş dönem borç kaydı yok.' }}
            columns={[
              {
                title: 'Tarih',
                dataIndex: 'incurred_at',
                render: (v) => v ? dayjs(v).format('DD.MM.YYYY') : '—',
              },
              { title: 'Açıklama', dataIndex: 'description' },
              {
                title: 'Tutar',
                dataIndex: 'amount',
                render: (v) => <span className="font-semibold">{parseFloat(v).toFixed(2)} ₺</span>,
              },
              {
                title: 'Durum',
                key: 'status',
                render: (_, r) => {
                  if (r.payment_status === 'paid') return <Tag color="success">Ödendi</Tag>;
                  if (r.payment_status === 'partial') {
                    return (
                      <Space orientation="vertical" size={0}>
                        <Tag color="warning">Kısmen Ödendi</Tag>
                        <Typography.Text type="danger" style={{ fontSize: 11 }}>
                          Kalan {parseFloat(r.remaining_amount).toFixed(2)} ₺
                        </Typography.Text>
                      </Space>
                    );
                  }
                  return <Tag>Ödenmedi</Tag>;
                },
              },
              ...(canManageDebts ? [{
                title: '',
                key: 'actions',
                width: 90,
                render: (_, r) => (
                  <Space>
                    <Button
                      size="small"
                      icon={<EditOutlined />}
                      title="Düzenle"
                      onClick={() => {
                        setEditingDebt(r);
                        debtForm.setFieldsValue({
                          amount:      parseFloat(r.amount),
                          incurred_at: r.incurred_at ? dayjs(r.incurred_at) : null,
                          description: r.description,
                          notes:       r.notes || '',
                        });
                        setAddDebtModal(true);
                      }}
                    />
                    {r.payment_status !== 'paid' && (
                      <Button
                        size="small"
                        type="primary"
                        icon={<CheckOutlined />}
                        title="Tahsilat ekle"
                        onClick={() => setPayDebtModal({ open: true, record: r, amount: r.remaining_amount, saving: false })}
                      />
                    )}
                    <Popconfirm
                      title="Bu borç kaydı silinsin mi?"
                      onConfirm={() => handleDeleteDebt(r)}
                      okText="Sil"
                      cancelText="Vazgeç"
                    >
                      <Button size="small" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                  </Space>
                ),
              }] : []),
            ]}
          />
        </Card>
      )}

      {/* Ödeme Geçmişi */}
      <Card
        title={
          <span className="flex items-center gap-2">
            <HistoryOutlined />
            Ödeme Geçmişi
          </span>
        }
      >
        <Table
          size="small"
          dataSource={payments}
          rowKey="id"
          loading={paymentsLoading}
          pagination={{ pageSize: 10, defaultCurrent: 1, showSizeChanger: false }}
          locale={{ emptyText: 'Henüz ödeme kaydı yok.' }}
          columns={[
            {
              title: 'Tarih',
              dataIndex: 'paid_at',
              render: (v, r) => dayjs(v || r.created_at).format('DD.MM.YYYY HH:mm'),
              defaultSortOrder: 'descend',
              sorter: (a, b) => dayjs(a.paid_at || a.created_at).unix() - dayjs(b.paid_at || b.created_at).unix(),
            },
            {
              title: 'Tutar',
              dataIndex: 'amount',
              render: (v) => <span className="font-semibold text-green-600">{parseFloat(v).toFixed(2)} ₺</span>,
            },
            {
              title: 'Yöntem',
              dataIndex: 'payment_method',
              render: (v) => <Tag color={METHOD_COLORS[v] || 'default'}>{PAYMENT_METHOD_LABEL[v] || v}</Tag>,
            },
            {
              title: 'Alan',
              dataIndex: 'received_by',
              render: (v) => {
                if (!v) return '—';
                const label = `${v.name} ${v.surname || ''}`.trim();
                return canViewUserDetail ? (
                  <Button type="link" style={{ padding: 0, height: 'auto' }} onClick={() => navigate(`/users/${v.id}`)}>
                    {label}
                  </Button>
                ) : label;
              },
            },
            {
              title: 'Not',
              dataIndex: 'notes',
              render: (v) => v || '—',
            },
            ...(!readOnly ? [{
              title: '',
              key: 'actions',
              width: 50,
              render: (_, r) => (
                <Button
                  size="small"
                  icon={<EditOutlined />}
                  onClick={() => {
                    setEditPayment(r);
                    paymentForm.setFieldsValue({
                      amount:         parseFloat(r.amount),
                      payment_method: r.payment_method,
                      paid_at:        dayjs(r.paid_at || r.created_at),
                      notes:          r.notes || '',
                    });
                  }}
                />
              ),
            }] : []),
          ]}
        />
      </Card>

      {/* Ödeme Düzenle Modal */}
      <Modal
        title="Ödeme Düzenle"
        open={!!editPayment}
        onCancel={() => { setEditPayment(null); paymentForm.resetFields(); }}
        footer={null}
        destroyOnClose
        width={420}
      >
        <Form form={paymentForm} layout="vertical" onFinish={handleSavePayment}>
          <Form.Item name="amount" label="Tutar (₺)" rules={[{ required: true, message: 'Tutar giriniz.' }]}>
            <InputNumber min={0.01} step={0.01} style={{ width: '100%' }} suffix="₺" />
          </Form.Item>
          <Form.Item name="payment_method" label="Ödeme Yöntemi" rules={[{ required: true, message: 'Yöntem seçiniz.' }]}>
            <Select>
              <Option value="cash">Nakit</Option>
              <Option value="transfer">Havale/EFT</Option>
              <Option value="credit_card">Kredi Kartı</Option>
              <Option value="other">Diğer</Option>
            </Select>
          </Form.Item>
          <Form.Item name="paid_at" label="Tarih" rules={[{ required: true, message: 'Tarih giriniz.' }]}>
            <DatePicker showTime style={{ width: '100%' }} format="DD.MM.YYYY HH:mm" />
          </Form.Item>
          <Form.Item name="notes" label="Not (opsiyonel)">
            <Input.TextArea rows={2} placeholder="Ödeme notu..." />
          </Form.Item>
          <Form.Item className="mb-0 flex justify-end">
            <Space>
              <Button onClick={() => { setEditPayment(null); paymentForm.resetFields(); }}>İptal</Button>
              <Button type="primary" htmlType="submit" loading={paymentSaving}>Kaydet</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Ödeme Al Modal */}
      <Modal
        title="Ödeme Al"
        open={modalOpen}
        onCancel={() => { setModalOpen(false); form.resetFields(); }}
        footer={null}
      >
        {totalDebt > 0 ? (
          <div className="mb-4 p-3 bg-red-50 rounded border border-red-200">
            <div className="text-red-600 font-medium">Kalan Borç: {totalDebt.toFixed(2)} ₺</div>
          </div>
        ) : (
          <div className="mb-4 p-3 bg-blue-50 rounded border border-blue-200">
            <div className="text-blue-600 font-medium text-sm">Mevcut borç yok — ödeme bakiyeye eklenir.</div>
          </div>
        )}
        <Form form={form} layout="vertical" onFinish={handlePayment}>
          <Form.Item name="amount" label="Tutar (₺)" rules={[{ required: true, message: 'Tutar giriniz.' }]}>
            <Input type="number" min="0.01" step="0.01" placeholder="0.00" suffix="₺" />
          </Form.Item>
          <Form.Item name="payment_method" label="Ödeme Yöntemi" rules={[{ required: true, message: 'Yöntem seçiniz.' }]}>
            <Select placeholder="Yöntem seçiniz">
              <Option value="cash">Nakit</Option>
              <Option value="transfer">Havale/EFT</Option>
              <Option value="credit_card">Kredi Kartı</Option>
              <Option value="other">Diğer</Option>
            </Select>
          </Form.Item>
          <Form.Item name="notes" label="Not (opsiyonel)">
            <Input.TextArea rows={2} placeholder="Ödeme notu..." />
          </Form.Item>
          <Form.Item className="mb-0 flex justify-end">
            <Space>
              <Button onClick={() => { setModalOpen(false); form.resetFields(); }}>İptal</Button>
              <Button type="primary" htmlType="submit" loading={odemeAliniyor}>Kaydet</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Geçmiş Dönem Borcu Ekle/Düzenle Modal */}
      <Modal
        title={editingDebt ? 'Geçmiş Dönem Borcunu Düzenle' : 'Geçmiş Dönem Borcu Ekle'}
        open={addDebtModal}
        onCancel={() => { setAddDebtModal(false); setEditingDebt(null); debtForm.resetFields(); }}
        footer={null}
        destroyOnClose
        width={420}
      >
        <Form form={debtForm} layout="vertical" onFinish={handleSaveDebt}>
          <Form.Item name="amount" label="Tutar (₺)" rules={[{ required: true, message: 'Tutar giriniz.' }]}>
            <InputNumber min={0.01} step={0.01} style={{ width: '100%' }} suffix="₺" />
          </Form.Item>
          <Form.Item name="incurred_at" label="Borcun Ait Olduğu Tarih (opsiyonel)">
            <DatePicker style={{ width: '100%' }} format="DD.MM.YYYY" />
          </Form.Item>
          <Form.Item name="description" label="Açıklama" rules={[{ required: true, message: 'Açıklama giriniz.' }]}>
            <Input placeholder="Örn. 2023 sertifika borcu" maxLength={255} />
          </Form.Item>
          <Form.Item name="notes" label="Not (opsiyonel)">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item className="mb-0 flex justify-end">
            <Space>
              <Button onClick={() => { setAddDebtModal(false); setEditingDebt(null); debtForm.resetFields(); }}>İptal</Button>
              <Button type="primary" htmlType="submit" loading={addDebtSaving}>Kaydet</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Geçmiş Dönem Borcuna Tahsilat Ekle Modal */}
      <Modal
        title="Tahsilat Ekle"
        open={payDebtModal.open}
        onCancel={() => setPayDebtModal({ open: false, record: null, amount: null, saving: false })}
        okText="Kaydet"
        cancelText="Vazgeç"
        confirmLoading={payDebtModal.saving}
        onOk={handlePayDebt}
      >
        {payDebtModal.record && (
          <Space orientation="vertical" style={{ width: '100%' }} size="middle">
            <Typography.Text type="secondary">
              {payDebtModal.record.description} — borç tutarı {parseFloat(payDebtModal.record.amount).toFixed(2)} ₺
            </Typography.Text>
            <div>
              <Typography.Text strong>Tahsil edilen tutar</Typography.Text>
              <InputNumber
                style={{ width: '100%', marginTop: 4 }}
                min={0.01}
                max={payDebtModal.record.remaining_amount}
                step={50}
                value={payDebtModal.amount}
                onChange={(v) => setPayDebtModal((s) => ({ ...s, amount: v }))}
                suffix="₺"
              />
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                Kalan borç {parseFloat(payDebtModal.record.remaining_amount).toFixed(2)} ₺. Daha azını
                girerseniz kayıt "kısmen ödendi" kalır.
              </Typography.Text>
            </div>
          </Space>
        )}
      </Modal>

      {/* Ders Ekle Modal */}
      <Modal
        title={<span><CalendarOutlined className="mr-2" />Ders Ekle — {student?.name} {student?.surname}</span>}
        open={lessonModal}
        onCancel={() => { setLessonModal(false); setLessonDate(null); setLessonClass(null); setLessonTime(null); setLessonType('one_time'); }}
        footer={null}
        destroyOnClose
        width={440}
      >
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">Kayıt Tipi</label>
          <Select style={{ width: '100%' }} value={lessonType} onChange={setLessonType}>
            <Option value="one_time">Tek Seferlik — Sadece o hafta</Option>
            <Option value="trial">Deneme — Sadece tek ders</Option>
            <Option value="makeup">Telafi — Sadece tek ders</Option>
            <Option value="regular">Normal — Her hafta bu güne eklenir</Option>
          </Select>
        </div>
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">Tarih</label>
          <DatePicker
            style={{ width: '100%' }}
            format="DD.MM.YYYY"
            value={lessonDate}
            onChange={setLessonDate}
          />
        </div>
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">Derslik</label>
          <Select style={{ width: '100%' }} placeholder="Derslik seçiniz" value={lessonClassroom} onChange={setLessonClass}>
            {classrooms.map(c => <Option key={c.id} value={c.id}>{c.name}</Option>)}
          </Select>
        </div>
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">Saat</label>
          <Select style={{ width: '100%' }} placeholder="Saat seçiniz" value={lessonTime} onChange={setLessonTime}>
            {lessonTimeOptions.map(t => <Option key={t} value={t}>{t}</Option>)}
          </Select>
        </div>
        <div className="flex justify-end gap-2">
          <Button onClick={() => { setLessonModal(false); setLessonDate(null); setLessonClass(null); setLessonTime(null); setLessonType('one_time'); }}>İptal</Button>
          <Button type="primary" loading={lessonLoading} disabled={!lessonDate || !lessonClassroom || !lessonTime} onClick={handleAddLesson}>
            Ekle
          </Button>
        </div>
      </Modal>
    </>
  );
};

export default StudentChargesTab;
